import { useMemo, useState } from "react";
import axios from "axios";
import { Check, ArrowRight, CreditCard, Lock, ShieldCheck } from "lucide-react";
import ScrollReveal from "./ScrollReveal";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const translateBackend = (t, data) => {
  if (!data) return t("msg_server_or_network");
  if (data.message_key) return t(data.message_key, data.message_params || {});
  return t("msg_unknown_error");
};

const PricingSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const plans = t("pricing.plans", { returnObjects: true });
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    name: "",
    number: "",
    expiry: "",
    cvc: "",
  });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState({ type: "", text: "" });

  const account = useMemo(() => ({
    application: localStorage.getItem("userApplication") || "",
    email: localStorage.getItem("userEmail") || "",
  }), []);

  const formatCardNumber = (value) =>
    value.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();

  const formatExpiry = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  };

  const openCheckout = (plan, index) => {
    const isFree = index === 0;
    const isEnterprise = index > 1;

    if (isFree) {
      navigate("/signup");
      return;
    }

    if (isEnterprise) {
      window.location.href = "mailto:contact@seza.ai";
      return;
    }

    setCheckoutPlan(plan);
    setPaymentMessage({ type: "", text: "" });
  };

  const closeCheckout = () => {
    if (paymentLoading) return;
    setCheckoutPlan(null);
    setPaymentMessage({ type: "", text: "" });
  };

  const handlePaymentChange = (field, value) => {
    setPaymentMessage({ type: "", text: "" });
    setPaymentForm((prev) => ({
      ...prev,
      [field]:
        field === "number"
          ? formatCardNumber(value)
          : field === "expiry"
          ? formatExpiry(value)
          : field === "cvc"
          ? value.replace(/\D/g, "").slice(0, 4)
          : value,
    }));
  };

  const activateSubscription = async (event) => {
    event.preventDefault();

    const application = account.application.trim();
    const cardDigits = paymentForm.number.replace(/\D/g, "");

    if (!application) {
      setPaymentMessage({ type: "error", text: t("pricing.payment.missingApp") });
      return;
    }

    if (!paymentForm.name.trim() || cardDigits.length < 12 || paymentForm.expiry.length < 5 || paymentForm.cvc.length < 3) {
      setPaymentMessage({ type: "error", text: t("pricing.payment.invalid") });
      return;
    }

    setPaymentLoading(true);
    setPaymentMessage({ type: "", text: "" });

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/subscriptions/mock-card`, {
        application,
        email: account.email,
        plan: "pro",
        card_last4: cardDigits.slice(-4),
        mock_payment: true,
      });

      localStorage.setItem("subscriptionPlan", "subscribed");
      setPaymentMessage({ type: "success", text: t("pricing.payment.success") });
    } catch (err) {
      setPaymentMessage({ type: "error", text: translateBackend(t, err?.response?.data) });
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <section id="pricing" className="pricing-section">
      <div className="pricing-container">
        <ScrollReveal className="pricing-header">
          <p className="pricing-label">{t("pricing.label")}</p>
          <h2 className="pricing-title">{t("pricing.title")}</h2>
          <p className="pricing-subtitle">{t("pricing.subtitle")}</p>
        </ScrollReveal>

        <ScrollReveal delay={80} className="pricing-top-benefits">
          <div className="pricing-top-item">
            <Check size={16} />
            <span>{t("pricing.noCreditCard")}</span>
          </div>

          <div className="pricing-separator">·</div>

          <div className="pricing-top-item">
            <Check size={16} />
            <span>{t("pricing.noCommitment")}</span>
          </div>

          <div className="pricing-separator">·</div>

          <div className="pricing-top-item">
            <Check size={16} />
            <span>{t("pricing.cancelAnytime")}</span>
          </div>
        </ScrollReveal>

        <div className="pricing-grid">
          {Array.isArray(plans) &&
            plans.map((plan, i) => {
              const highlighted = i === 1;

              return (
                <ScrollReveal key={i} delay={i * 100}>
                  <div
                    className={`pricing-card ${
                      highlighted ? "pricing-card-highlighted" : ""
                    }`}
                  >
                    {plan.badge && (
                      <div className="pricing-badge">{plan.badge}</div>
                    )}

                    <div className="pricing-card-header">
                      <h3 className="pricing-plan-name">{plan.name}</h3>
                      <p className="pricing-plan-description">
                        {plan.description}
                      </p>

                      <div className="pricing-price-row">
                        <span className="pricing-price">{plan.price}</span>
                        {plan.period && (
                          <span className="pricing-period">{plan.period}</span>
                        )}
                      </div>
                    </div>

                    <ul className="pricing-features">
                      {Array.isArray(plan.features) &&
                        plan.features.map((feature, j) => (
                          <li key={j} className="pricing-feature-item">
                            <Check size={16} className="pricing-feature-icon" />
                            <span>{feature}</span>
                          </li>
                        ))}
                    </ul>

                    <button
                      type="button"
                      onClick={() => openCheckout(plan, i)}
                      className={`pricing-button ${
                        highlighted
                          ? "pricing-button-primary"
                          : "pricing-button-secondary"
                      }`}
                    >
                      {plan.cta}
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </ScrollReveal>
              );
            })}
        </div>
      </div>

      {checkoutPlan && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="payment-title">
          <div className="payment-modal">
            <button
              type="button"
              className="qr-upgrade-close"
              onClick={closeCheckout}
              aria-label={t("close")}
            >
              x
            </button>

            <div className="payment-panel payment-panel-summary">
              <p className="payment-eyebrow">{t("pricing.payment.demoBadge")}</p>
              <h3 id="payment-title" className="payment-title">
                {t("pricing.payment.title")}
              </h3>
              <p className="payment-copy">{t("pricing.payment.subtitle")}</p>

              <div className="payment-plan-box">
                <span>{checkoutPlan.name}</span>
                <strong>{checkoutPlan.price}{checkoutPlan.period}</strong>
              </div>

              <div className="payment-trust-list">
                <span><ShieldCheck size={16} /> {t("pricing.payment.mockOnly")}</span>
                <span><Lock size={16} /> {t("pricing.payment.noCharge")}</span>
                <span><CreditCard size={16} /> {t("pricing.payment.activateNow")}</span>
              </div>
            </div>

            <form className="payment-panel payment-form" onSubmit={activateSubscription}>
              <label className="payment-label">
                {t("pricing.payment.cardName")}
                <input
                  className="payment-input"
                  value={paymentForm.name}
                  onChange={(e) => handlePaymentChange("name", e.target.value)}
                  placeholder={t("pricing.payment.cardNamePlaceholder")}
                  autoComplete="cc-name"
                />
              </label>

              <label className="payment-label">
                {t("pricing.payment.cardNumber")}
                <input
                  className="payment-input payment-input-card"
                  value={paymentForm.number}
                  onChange={(e) => handlePaymentChange("number", e.target.value)}
                  placeholder="4242 4242 4242 4242"
                  inputMode="numeric"
                  autoComplete="cc-number"
                />
              </label>

              <div className="payment-row">
                <label className="payment-label">
                  {t("pricing.payment.expiry")}
                  <input
                    className="payment-input"
                    value={paymentForm.expiry}
                    onChange={(e) => handlePaymentChange("expiry", e.target.value)}
                    placeholder="MM/YY"
                    inputMode="numeric"
                    autoComplete="cc-exp"
                  />
                </label>

                <label className="payment-label">
                  {t("pricing.payment.cvc")}
                  <input
                    className="payment-input"
                    value={paymentForm.cvc}
                    onChange={(e) => handlePaymentChange("cvc", e.target.value)}
                    placeholder="123"
                    inputMode="numeric"
                    autoComplete="cc-csc"
                  />
                </label>
              </div>

              <p className="payment-note">{t("pricing.payment.demoNotice")}</p>

              {paymentMessage.text && (
                <p className={`payment-message ${paymentMessage.type === "success" ? "payment-message-success" : "payment-message-error"}`}>
                  {paymentMessage.text}
                </p>
              )}

              <div className="payment-actions">
                {paymentMessage.type === "success" ? (
                  <button type="button" className="payment-primary" onClick={() => navigate("/qr-generator")}>
                    {t("pricing.payment.backToQr")}
                  </button>
                ) : (
                  <>
                    <button type="button" className="payment-secondary" onClick={closeCheckout}>
                      {t("cancel")}
                    </button>
                    <button type="submit" className="payment-primary" disabled={paymentLoading}>
                      {paymentLoading ? t("pricing.payment.processing") : t("pricing.payment.payButton")}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default PricingSection;
